import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { WatchlistService } from "../services/watchlist-service";
import type { Asset, Watchlist, WatchlistItem } from "@/schemas";
import { toast } from "sonner";
import { Check, Loader2, Plus, Bookmark } from "lucide-react";
import { cn } from "@/lib/utils";
import { AssetLogo } from "@/components/asset/asset-logo";

interface WatchlistWithMembership extends Watchlist {
  hasAsset: boolean;
  existingItem?: WatchlistItem;
}

interface AddToWatchlistModalProps {
  asset: Asset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddToWatchlistModal({
  asset,
  open,
  onOpenChange,
}: AddToWatchlistModalProps) {
  const [watchlists, setWatchlists] = useState<WatchlistWithMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialSelectedIds, setInitialSelectedIds] = useState<Set<string>>(
    new Set(),
  );
  const [targetPrice, setTargetPrice] = useState("");
  const [saving, setSaving] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [creatingList, setCreatingList] = useState(false);

  useEffect(() => {
    if (open && asset) {
      loadWatchlistsWithMembership(asset.asset_id);
      setNewListName("");
    }
  }, [open, asset]);

  const loadWatchlistsWithMembership = async (assetId: string) => {
    try {
      setLoading(true);
      const allWatchlists = await WatchlistService.list();

      // Check each watchlist for the asset
      const watchlistsWithMembership: WatchlistWithMembership[] =
        await Promise.all(
          allWatchlists.map(async (wl) => {
            try {
              const items = await WatchlistService.items(wl.watchlist_id);
              const existingItem = items.find(
                (item) => item.asset_id === assetId,
              );
              return {
                ...wl,
                hasAsset: !!existingItem,
                existingItem,
              };
            } catch {
              return { ...wl, hasAsset: false };
            }
          }),
        );

      setWatchlists(watchlistsWithMembership);

      // Pre-select watchlists that already have the asset
      const alreadyInIds = new Set(
        watchlistsWithMembership
          .filter((wl) => wl.hasAsset)
          .map((wl) => wl.watchlist_id),
      );
      setSelectedIds(alreadyInIds);
      setInitialSelectedIds(alreadyInIds);

      // Set target price from existing item if available
      const existingWithPrice = watchlistsWithMembership.find(
        (wl) => wl.existingItem?.target_price != null,
      );
      if (existingWithPrice?.existingItem?.target_price) {
        setTargetPrice(existingWithPrice.existingItem.target_price.toString());
      } else {
        setTargetPrice("");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load watchlists");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (watchlistId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(watchlistId)) {
        next.delete(watchlistId);
      } else {
        next.add(watchlistId);
      }
      return next;
    });
  };

  const handleCreateList = async () => {
    const name = newListName.trim();
    if (!name) return;

    try {
      setCreatingList(true);
      const created = await WatchlistService.create(name);
      setWatchlists((prev) => [...prev, { ...created, hasAsset: false }]);
      setSelectedIds((prev) => new Set(prev).add(created.watchlist_id));
      setNewListName("");
      toast.success("Watchlist created");
    } catch (error) {
      console.error(error);
      toast.error("Failed to create watchlist");
    } finally {
      setCreatingList(false);
    }
  };

  const handleSave = async () => {
    if (!asset) return;

    try {
      setSaving(true);
      const price = targetPrice.trim() ? parseFloat(targetPrice) : undefined;
      const payload: { asset_id: string; target_price?: number } = {
        asset_id: asset.asset_id,
      };
      if (price && !isNaN(price)) {
        payload.target_price = price;
      }

      let addedCount = 0;
      let removedCount = 0;
      const errors: string[] = [];

      // Add to newly selected watchlists
      for (const watchlistId of selectedIds) {
        if (!initialSelectedIds.has(watchlistId)) {
          try {
            await WatchlistService.addItem(watchlistId, payload);
            addedCount++;
          } catch (error) {
            console.error(error);
            errors.push(error instanceof Error ? error.message : "Failed to add item");
          }
        }
      }

      // Remove from unselected watchlists
      for (const watchlistId of initialSelectedIds) {
        if (!selectedIds.has(watchlistId)) {
          const wl = watchlists.find((w) => w.watchlist_id === watchlistId);
          if (wl?.existingItem) {
            try {
              await WatchlistService.removeItem(
                watchlistId,
                wl.existingItem.item_id,
              );
              removedCount++;
            } catch (error) {
              console.error(error);
              errors.push(error instanceof Error ? error.message : "Failed to delete item");
            }
          }
        }
      }

      // Update target price for existing items if changed
      if (price) {
        for (const watchlistId of selectedIds) {
          if (initialSelectedIds.has(watchlistId)) {
            const wl = watchlists.find((w) => w.watchlist_id === watchlistId);
            if (wl?.existingItem && wl.existingItem.target_price !== price) {
              try {
                await WatchlistService.updateItem(
                  watchlistId,
                  wl.existingItem.item_id,
                  {
                    target_price: price,
                  },
                );
              } catch (error) {
                console.error(error);
                errors.push(error instanceof Error ? error.message : "Failed to update item");
              }
            }
          }
        }
      }

      if (addedCount > 0) {
        toast.success(
          `Added to ${addedCount} watchlist${addedCount > 1 ? "s" : ""}`,
        );
      }
      if (removedCount > 0) {
        toast.success(
          `Removed from ${removedCount} watchlist${removedCount > 1 ? "s" : ""}`,
        );
      }
      if (addedCount === 0 && removedCount === 0) {
        toast.info("No changes made");
      }
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }

      onOpenChange(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update watchlists");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    selectedIds.size !== initialSelectedIds.size ||
    [...selectedIds].some((id) => !initialSelectedIds.has(id)) ||
    [...initialSelectedIds].some((id) => !selectedIds.has(id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="h-5 w-5 text-primary" />
            Add to Watchlist
          </DialogTitle>
          {asset && (
            <div className="flex items-center gap-1.5 pt-1">
              <AssetLogo asset={asset} size="xs" />
              <span className="font-medium text-sm">{asset.symbol}</span>
            </div>
          )}
          <DialogDescription>
            Choose one or more watchlists for this asset
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Target Price */}
          <div className="space-y-2">
            <Label htmlFor="target-price">Target Price (optional)</Label>
            <Input
              id="target-price"
              type="number"
              placeholder="e.g. 150.00"
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              If set, this price will be shared across all your watchlists for
              this asset.
            </p>
          </div>

          {/* Watchlist Selection */}
          <div className="space-y-2">
            <Label>Select Watchlists</Label>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : watchlists.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No watchlists yet. Create one below.
              </p>
            ) : (
              <ScrollArea className="h-48 rounded-md border">
                <div className="p-2 space-y-1">
                  {watchlists.map((wl) => {
                    const isSelected = selectedIds.has(wl.watchlist_id);
                    return (
                      <button
                        key={wl.watchlist_id}
                        type="button"
                        onClick={() => handleToggle(wl.watchlist_id)}
                        className={cn(
                          "w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                          isSelected
                            ? "bg-primary/10 text-primary"
                            : "hover:bg-muted",
                        )}
                      >
                        <div
                          className={cn(
                            "flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
                            isSelected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-muted-foreground/30",
                          )}
                        >
                          {isSelected && <Check className="h-3.5 w-3.5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium truncate">{wl.name}</p>
                            {wl.hasAsset && (
                              <Badge variant="secondary" className="text-xs">
                                Added
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {wl.items_count ?? 0} item
                            {(wl.items_count ?? 0) !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Create New List */}
          <div className="flex gap-2">
            <Input
              placeholder="New watchlist name..."
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreateList();
                }
              }}
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleCreateList}
              disabled={creatingList || !newListName.trim()}
            >
              {creatingList ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || (!hasChanges && !targetPrice)}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Bookmark className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
